<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  DEFAULT_PAGE_DIMENSION,
  IElement,
  PageElementOptions,
} from '@gohighlevel/ghl-proposals-common'
import BlocksRenderer from '../common/BlocksRenderer.vue'
import FloatingElementRenderer from '../common/FloatingElementRenderer.vue'

const props = defineProps<{
  page: IElement<PageElementOptions>
  elements: IElement[]
}>()
const pageParentRef = ref<HTMLElement>()

const getDraggableElement = (children: IElement[]) => {
  if (!children) return []
  return children.filter(element => element && element?.component?.isDraggable)
}

const pageContentHeight = computed(() => {
  if (props.page.version > 1) {
    const { dimensions, margins } =
      props.page?.component.options.pageDimensions || DEFAULT_PAGE_DIMENSION
    return `${dimensions.height - margins.top - margins.bottom}px`
  }
  return DEFAULT_PAGE_DIMENSION.dimensions.height + 'px'
})

watch(
  pageParentRef,
  updatedPageRef => {
    pageParentRef.value = updatedPageRef
  },
  { immediate: true }
)
</script>

<template>
  <div class="parent">
    <div
      class="document-block"
      style="flex: 1 1 0%; width: 100%; display: flex; flex-direction: column"
    >
      <FloatingElementRenderer
        :elements="getDraggableElement(page.children)"
        :pageId="page.id"
        :page="props.page"
        :pageRef="pageParentRef"
        :parentRef="pageParentRef"
      />
      <template
        v-for="element in props.elements"
        v-bind:key="'NestedElementRenderer-' + element.id"
      >
        <BlocksRenderer
          :page="page"
          :pageRef="pageParentRef"
          :parentRef="pageParentRef"
          :element="(element as IElement<any>)"
          :pageId="(page.id as string)"
          v-if="!element.component.isDraggable"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.page-content {
  min-height: v-bind('pageContentHeight');
  width: 816px;
  display: flex;
  flex-direction: column;
}
</style>
